import { useState } from 'react';
import { useTheme } from 'styled-components';
import { BaseChartProps, ChartData, ChartState, getDataVizColor } from '@mews/b2b-ui';
import { WithChildren } from '@optimus-web/core';
import { BackgroundCircle, CircularContainer, LastSegmentRoundEnding, ProgressChartContent, Segment } from './styles';

const getSegmentProportion = (segmentValue: number, total: number) => (segmentValue / total) * 100;

const getSegmentLength = (circumference: number, segmentProportion: number, segmentGapSize: number) =>
    (circumference * segmentProportion) / 100 - segmentGapSize;

export interface CircularProgressProps extends Omit<BaseChartProps, 'minWidth'>, WithChildren {
    minWidth?: number;
    strokeWidth?: number;
    data: ChartData[];
    total: number;
    segmentGapSize?: number;
}

export const CircularProgressChart: React.FC<CircularProgressProps> = ({
    minWidth = 160,
    strokeWidth = 3,
    segmentGapSize = 2,
    data,
    total,
    animated = true,
    children,
    loading,
    empty,
    error,
}) => {
    const theme = useTheme();
    const [animationEnded, setAnimationEnded] = useState(false);

    const displayChartState = loading || error || empty;

    if (displayChartState) {
        return <ChartState empty={empty} error={error} />;
    }

    const center = minWidth / 2;
    const radius = minWidth / 2 - strokeWidth / 2;
    const circumference = 2 * Math.PI * radius;

    let cumulativeOffset = circumference;
    let segmentValueSum = 0;

    return (
        <div
            style={{
                position: 'relative',
                width: minWidth,
                height: minWidth,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
            }}
        >
            <CircularContainer width={minWidth} height={minWidth}>
                <BackgroundCircle cx={center} cy={center} r={radius} strokeWidth={strokeWidth} />
                {data.map((segment, index) => {
                    const segmentProportion = getSegmentProportion(segment.value, total);
                    const segmentLength = getSegmentLength(circumference, segmentProportion, segmentGapSize);
                    const color = getDataVizColor(index, segment.color)({ theme });
                    const isLastSegment = index === data.length - 1;

                    const offset = cumulativeOffset;
                    cumulativeOffset -= segmentLength + segmentGapSize;
                    segmentValueSum += segment.value;

                    const sharedProps = {
                        cx: center,
                        cy: center,
                        r: radius,
                        color,
                        animated,
                        segmentLength,
                        circumference,
                        strokeDashoffset: offset,
                        strokeWidth,
                    };

                    return (
                        <>
                            <Segment
                                key={segment.id}
                                segmentGapSize={segmentGapSize}
                                onAnimationEnd={() => isLastSegment && setAnimationEnded(true)}
                                {...sharedProps}
                            />
                            {isLastSegment && segmentValueSum !== total && segmentValueSum !== 0 && (
                                <LastSegmentRoundEnding
                                    segmentGapSize={0}
                                    onAnimationEnd={() => setAnimationEnded(true)}
                                    {...sharedProps}
                                />
                            )}
                        </>
                    );
                })}
            </CircularContainer>
            <ProgressChartContent show={animationEnded} animated={animated}>
                {children}
            </ProgressChartContent>
        </div>
    );
};
